import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { storageService } from '../../services/storageService';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import FileUpload from '../../components/ui/FileUpload';

const BusinessProfile: React.FC = () => {
  const navigate = useNavigate();
  const [businessName, setBusinessName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [address, setAddress] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { createBusinessProfile, signOut, businessId, loading: authLoading } = useAuth();

  // Check if user already has a business, redirect to dashboard if they do
  useEffect(() => {
    if (!authLoading && businessId) {
      console.log('BusinessProfile: User already has business, redirecting to dashboard');
      navigate('/dashboard', { replace: true });
    }
  }, [businessId, authLoading, navigate]);

  const handleLogoSelect = (file: File) => {
    setLogoFile(file);
    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setLogoPreview(previewUrl);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remove all non-numeric characters except the +62 prefix
    const value = e.target.value.replace(/[^\d]/g, '');
    
    // Ensure it starts with the Indonesian country code
    if (value.startsWith('62')) {
      setPhoneNumber('+' + value);
    } else if (value.startsWith('0')) {
      // Convert leading 0 to +62
      setPhoneNumber('+62' + value.substring(1));
    } else if (value) {
      setPhoneNumber('+62' + value);
    } else {
      setPhoneNumber('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setError('');
      setLoading(true);
      
      let logoUrl = null;
      
      // Upload logo if selected
      if (logoFile) {
        const tempBusinessId = 'temp-' + Date.now();
        const { url, error: uploadError } = await storageService.uploadBusinessLogo(
          logoFile,
          tempBusinessId
        );
        
        if (uploadError) throw uploadError;
        logoUrl = url;
      }
      
      // Create business profile
      const { business, error } = await createBusinessProfile({
        name: businessName,
        address,
        phone: phoneNumber,
        logo_url: logoUrl,
      });
      
      if (error) {
        setError(error.message);
      } else {
        // Business profile created successfully
        console.log('Business profile created successfully');

        // Wait a moment for the auth context to update, then navigate
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 1500);
      }
    } catch (err) {
      console.error('Business profile creation error:', err);
      if (err instanceof Error) {
        setError(`Failed to create business profile: ${err.message}`);
      } else {
        setError('Failed to create business profile. Please try again.');
      }
    }
    
    setLoading(false);
  };

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (err) {
      console.error('Failed to sign out:', err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto">
        <div className="bg-white py-8 px-4 shadow rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <h2 className="text-2xl font-bold text-center text-gray-900">Create Business Profile</h2>
            <p className="text-center text-gray-600">
              Set up your repair business information
            </p>
            
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
                {error}
              </div>
            )}
            
            {/* Logo Upload - At the top */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Logo (Optional)
              </label>
              <FileUpload
                onFileSelect={handleLogoSelect}
                currentImageUrl={logoPreview || undefined}
                placeholder="Upload your business logo"
                maxSize={2}
              />
              <p className="mt-1 text-xs text-gray-500">
                Recommended: PNG or JPG, max 2MB, square aspect ratio
              </p>
            </div>
            
            {/* Business Name */}
            <Input
              label="Business Name"
              type="text"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              required
            />
            
            {/* Business Phone - Indonesian format */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business Phone
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">🇮🇩 +62</span>
                </div>
                <input
                  type="tel"
                  value={phoneNumber.replace('+62', '')}
                  onChange={handlePhoneChange}
                  className="input-field pl-16"
                  placeholder="81234567890"
                  required
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Enter your Indonesian phone number without the country code
              </p>
            </div>
            
            {/* Business Address - Multi-line */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business Address
              </label>
              <textarea
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                rows={3}
                className="input-field resize-vertical"
                placeholder="Enter your complete business address"
                required
              />
            </div>
            
            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={loading || !businessName.trim() || !phoneNumber.trim() || !address.trim()}
            >
              {loading ? 'Creating profile...' : 'Create Business Profile'}
            </Button>
          </form>
        </div>
        
        {/* Logout Button - Below the form */}
        <div className="mt-6 text-center">
          <button
            onClick={handleLogout}
            className="text-sm text-gray-600 hover:text-gray-900 underline focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Sign out instead
          </button>
        </div>
      </div>
    </div>
  );
};

export default BusinessProfile;